import Image from "next/image";
import { useRouter } from "next/router";
import { useCallback } from "react";

import { BoxProps, Center, Flex, Tag, Text } from "@chakra-ui/react";

import UTMLink from "@components/UTMLink";
import { LSTSidebarOptions } from "@constants/common";
import { SideBarDataType } from "@constants/types";
import useDappConfig from "@hooks/useDappConfig";

interface SidebarProps extends BoxProps {}

const Sidebar = ({ ...rest }: SidebarProps) => {
  const router = useRouter();

  const { swapEnabledChains } = useDappConfig();

  const getSideBarItem = useCallback(
    (option: SideBarDataType) => {
      const { key, title, isComingSoon, isDisabled, link, isExternal } = option;

      const getIcon = () => {
        switch (key) {
          case "restake":
            return "/assets/sidebar/restake.svg";
          case "dashboard":
            return "/assets/sidebar/dashboard.svg";
          case "defi":
            return "/assets/sidebar/defi.svg";
          case "bridge":
            return "/assets/sidebar/bridge.svg";
          case "claim-kep":
            return "/assets/sidebar/claim-kep.svg";
          case "wrap":
            return "/assets/sidebar/wrap.svg";
        }
        return "";
      };

      const [, activeKey] = router?.pathname?.split("/") || "";

      return (
        <UTMLink key={key} href={link} isExternal={isExternal}>
          <Flex
            p="12px 16px 12px 12px"
            borderRadius="4px"
            gap="12px"
            bg={activeKey === key ? "backgroundDivider" : "inherit"}
            alignItems="flex-start"
            cursor={isDisabled ? "not-allowed" : "pointer"}
          >
            <Image
              src={getIcon()}
              alt={key}
              height={24}
              width={23}
              loading="eager"
            />
            <Flex flexDir="column">
              <Text
                color={activeKey === key ? "textPrimary" : "textSecondary"}
                fontWeight="500"
                fontSize="16px"
                lineHeight="24px"
              >
                {title}
              </Text>
              {isComingSoon && (
                <Tag
                  bg={{ base: "backgroundCanvas", xl: "backgroundCard" }}
                  color="textPrimary"
                  fontWeight="600"
                  fontSize="10px"
                  p="0px 4px"
                  borderRadius="4px"
                  lineHeight="12px"
                  letterSpacing="0.4px"
                  w="max-content"
                  minH="16px"
                  ml="4px"
                >
                  COMING SOON
                </Tag>
              )}
            </Flex>
          </Flex>
        </UTMLink>
      );
    },
    [router]
  );

  const filteredOptions = swapEnabledChains.length
    ? LSTSidebarOptions
    : LSTSidebarOptions.filter((opt) => opt.key !== "wrap");

  return (
    <Flex
      flexDir="column"
      justifyContent="flex-start"
      gap={{ base: "40px", xl: "64px" }}
      p={{ base: "32px 24px 0 40px", xl: "0 28px 0 40px" }}
      {...rest}
      position="fixed"
      w={{ base: "full", md: "240px" }}
    >
      <UTMLink href="/">
        <Center h="40px" justifyContent="flex-start" cursor="pointer">
          <Image
            src="/assets/kelp.svg"
            alt="kelp"
            height={40}
            width={106}
            loading="eager"
          />
        </Center>
      </UTMLink>

      <Flex
        flexDir="column"
        gap="12px"
        w="172px"
        mt={{ base: "0px", xl: "64px" }}
      >
        <Text
          fontWeight="600"
          fontSize="12px"
          lineHeight="18px"
          color="textSecondary"
          textTransform="uppercase"
          pl="12px"
        >
          Liquid Restaking
        </Text>

        {filteredOptions.map((option) => getSideBarItem(option))}
      </Flex>
    </Flex>
  );
};

export default Sidebar;
